'use client';

import Link from 'next/link';
import { Button } from '@/components/ui/button';
import '@/styles/user-store-setup.css';
import { useEffect } from 'react';
import { captureException } from '@sentry/nextjs';
import { useErrorTracking } from '@/hooks';

const ErrorPage = ({ error, reset }: { error: Error & { digest?: string }; reset: () => void }) => {
  const { captureError } = useErrorTracking();

  const refreshPage = () => {
    window.location.reload();
  };

  useEffect(() => {
    captureException(error, {
      tags: {
        errorType: 'component_error',
        errorDigest: error.digest || 'no-digest'
      },
      level: 'error' // Not as severe as global errors
    });

    // The same crash to PostHog, with the user + org attached. `captureError` is
    // stable, so listing it can't cause a re-report.
    captureError(error, {
      error_source: 'app_error_boundary',
      error_code: error.digest || 'no-digest',
      error_message: error.message,
      failure_layer: 'client'
    });
  }, [error, reset, captureError]);

  return (
    <div className="flex flex-col justify-center items-center gap-6 h-screen w-screen user-store-setup">
      <div className="text-slate-900 drop-shadow-[6px_0px_0px_#542cc2] sm:drop-shadow-[10px_0px_0px_#542cc2] text-[100px] sm:text-[200px] md:text-[300px] font-bold leading-[80px] sm:leading-[160px] md:leading-[240px]">
        503
      </div>
      <div className="flex flex-col items-center gap-2 text-gray-950">
        <div className="text-2xl sm:text-4xl font-bold leading-6 sm:leading-10">
          Service Unavailable
        </div>
        <div className="w-[360px] sm:w-[644px] text-center text-sm sm:text-lg font-normal leading-5 sm:leading-7">
          {`Our servers are temporarily unavailable. We’re working hard to resolve the issue. Please try again later or contact support if the problem persists.`}
        </div>
      </div>
      <div className="flex gap-6">
        <Link href="mailto:hello@strique.io" rel="noopener noreferrer" target="_blank">
          <Button
            variant="outline"
            className="h-10 px-4 py-3 rounded-lg text-sm font-medium leading-tight">
            Contact Support
          </Button>
        </Link>
        <Button
          onClick={refreshPage}
          className="h-10 px-4 py-3 rounded-lg text-sm font-medium leading-tight">
          Refresh page
        </Button>
      </div>
    </div>
  );
};

export default ErrorPage;
